// src/ui/fear-feedback/FearFeedbackLayer.tsx — Phase 6 Plan 06-07 fear layer.
//
// The "invisible half" of the cue layer (D-13). Three continuous channels
// drive off the LOCAL player's Entity.fear value (D-15 — local-player only;
// NPC fear has no UI consequence):
//   1. Vignette opacity      — radial-gradient alpha via --vignette-opacity
//   2. Heartbeat BPM         — Howl.rate on a single sample via audioManager
//   3. Sprite shiver delta   — additive (dx, dy) on the rendered player sprite
//
// All three are recomputed per requestAnimationFrame from the latest fears
// payload received over the renderBus 'ai-debug' event. The 30 Hz sim tick
// produces ai-debug snapshots; we lerp between consecutive samples for a
// smooth 60 Hz feel even on fast fear changes.
//
// 'sim-tick' is consumed for playerStatus — when the local player flips to
// `caught`, the layer fades to zero over P6_FEAR_FADE_OUT_ON_CAUGHT_MS.
// Heartbeat audio is NOT part of the caught fade (UI-SPEC §FearFeedbackLayer:
// "audio still plays" — diegetic continuity through the catch beat).
//
// prefers-reduced-motion zeros the visual channels (vignette + shiver) but
// leaves the heartbeat audio alone. The CSS file ALSO sets `background:
// transparent` under the same media query so the layer is doubly-protected
// against motion-sensitivity (CSS handles the case where JS hasn't read
// matchMedia yet, e.g. SSR shell or transient first paint).
//
// SOURCE: 06-CONTEXT.md D-13/14/15/16; 06-UI-SPEC.md §FearFeedbackLayer;
//         06-PATTERNS.md lines 397-428.

import { useEffect, useRef, type JSX } from 'react';
import './fear-feedback.css';

import { renderBus } from '../../render/bus';
import { audioManager } from '../../render/audio/manager';
import { getGameSceneRef } from '../../render/PhaserGame';
import {
  P6_SHIVER_HZ,
  P6_FEAR_FADE_OUT_ON_CAUGHT_MS,
  P6_SHIVER_DECAY_MS,
  P6_FEAR_IMPULSE_DELTA,
} from '../../render/audio/constants';
import {
  fearToVignetteOpacity,
  fearToHeartbeatBpm,
  fearToShiverAmplitudePx,
} from './fear-curve';

// PLAYER_ENTITY_ID = 'e0' — D-15 local-player gate. The literal 'e0' is
// inlined at the fears?.['e0'] lookup site (matches the project-wide pattern
// used by Phase 4 ai-debug subscriptions); kept as a top-of-file comment so
// future readers can locate the entity-id contract via grep without an
// unused constant declaration tripping the strict noUnusedLocals.

export function FearFeedbackLayer(): JSX.Element {
  const vignetteRef = useRef<HTMLDivElement | null>(null);
  // Capture once at mount — re-subscribing on a per-frame basis would be
  // wasteful, and the user does not toggle prefers-reduced-motion mid-session.
  const reducedMotion = useRef<boolean>(
    typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ).current;

  useEffect(() => {
    // 30 Hz sim → 60 Hz render lerp pair. lastFear / nextFear are the two
    // most-recently-sampled e0 fear values; lastSampleMs marks when the
    // nextFear arrived so the per-rAF interpolation can map elapsed time
    // back into a [0..1] alpha for the lerp.
    let lastFear = 0;
    let nextFear = 0;
    let lastSampleMs = performance.now();
    let isCaught = false;
    let caughtFadeStartMs: number | null = null;

    // Plan 06-11 (UAT Gap 2): post-event shiver-decay envelope. The shiver
    // channel must release within P6_SHIVER_DECAY_MS of the LAST fear-impulse
    // regardless of the slow sim-side fear decay (~0.03/sec) — otherwise a
    // single nearby catch keeps the sprite oscillating at 4Hz for 8-15s,
    // matching the user's ">10 seconds wobble" UAT complaint.
    //
    // Envelope state (per-effect, captured in this closure):
    //   envelopePeakPx     — amplitude (px) captured at the moment of impulse;
    //                        the envelope linearly decays from this to 0.
    //   envelopeStartedMs  — wall-clock time the current envelope started; null
    //                        when no envelope is active.
    //
    // Impulse detection uses the M7-PINNED delta-based heuristic
    // (see src/render/audio/constants.ts P6_FEAR_IMPULSE_DELTA): re-arm only
    // when (nextFear - lastFear) > P6_FEAR_IMPULSE_DELTA. This correctly fires
    // on rapid-onset catch events (delta ~0.4-0.6) while NOT spuriously
    // re-triggering on every steady-state ai-debug tick above NERVOUS — a
    // threshold-based heuristic would defeat the decay.
    //
    // The vignette + heartbeat channels are NOT enveloped — they remain tied
    // to raw `interpolated` fear so the slower-tempo cozy-nervous cues (D-13)
    // are preserved.
    let envelopePeakPx = 0;
    let envelopeStartedMs: number | null = null;

    const offAi = renderBus.on('ai-debug', (payload) => {
      lastFear = nextFear;
      // D-15: local-player only. NPC fears in payload.fears[*] are ignored.
      // The literal `fears?.['e0']` shape is the project-wide pattern used
      // for the Phase 4 ai-debug subscriptions; PLAYER_ENTITY_ID is 'e0'.
      nextFear = payload.fears?.['e0'] ?? 0;
      lastSampleMs = performance.now();

      // Plan 06-11 / M7-pinned: detect fear-impulse-up via strict-greater-than
      // delta heuristic. Re-arm captures the larger of (current envelope,
      // post-impulse amplitude) so a partially-decayed envelope hit by a new
      // impulse JUMPS UP rather than restarting from a smaller post-decay
      // value. Decay timer always restarts from now().
      if ((nextFear - lastFear) > P6_FEAR_IMPULSE_DELTA) {
        const newPeak = Math.max(envelopePeakPx, fearToShiverAmplitudePx(nextFear));
        envelopePeakPx = newPeak;
        envelopeStartedMs = performance.now();
      }
    });

    const offTick = renderBus.on('sim-tick', (payload) => {
      // P3 D-08 sim-tick payload extension carries playerStatus.
      const status = (payload as { playerStatus?: string }).playerStatus;
      if (status === 'caught' && !isCaught) {
        isCaught = true;
        caughtFadeStartMs = performance.now();
      } else if (status !== 'caught' && isCaught) {
        isCaught = false;
        caughtFadeStartMs = null;
      }
    });

    let raf = 0;
    function tick(): void {
      // 30 Hz sim period in ms; the t scalar is clamped at 1 so a stale
      // sample (e.g. paused tab catching up) doesn't extrapolate.
      const t = (performance.now() - lastSampleMs) / (1000 / 30);
      const interpolated = lastFear + (nextFear - lastFear) * Math.min(1, t);

      // Caught fade-out scale (D-15). Multiplied into vignette + shiver but
      // NOT into the heartbeat BPM — the audio survives the catch beat
      // (UI-SPEC §FearFeedbackLayer "heartbeat audio still plays").
      let scale = 1;
      if (isCaught && caughtFadeStartMs !== null) {
        const elapsed = performance.now() - caughtFadeStartMs;
        scale = Math.max(
          0,
          1 - elapsed / P6_FEAR_FADE_OUT_ON_CAUGHT_MS,
        );
      }

      const opacity = reducedMotion
        ? 0
        : fearToVignetteOpacity(interpolated) * scale;
      const bpm = fearToHeartbeatBpm(interpolated);

      // Plan 06-11 (UAT Gap 2): compute the shiver-decay envelope's current
      // amplitude in px, then clamp the raw-fear-driven shiver against it.
      // When the envelope is inactive (no recent impulse), currentEnvelopePx
      // stays 0 — which clamps the shiver to 0 even if `interpolated` fear
      // is high (e.g. mid sim-side decay after a catch event). When the
      // envelope is active, it linearly decays from envelopePeakPx to 0 over
      // P6_SHIVER_DECAY_MS, then clears itself.
      let currentEnvelopePx = 0;
      if (envelopeStartedMs !== null) {
        const elapsed = performance.now() - envelopeStartedMs;
        if (elapsed >= P6_SHIVER_DECAY_MS) {
          currentEnvelopePx = 0;
          envelopeStartedMs = null;
          envelopePeakPx = 0;
        } else {
          currentEnvelopePx = envelopePeakPx * (1 - elapsed / P6_SHIVER_DECAY_MS);
        }
      }
      const shiverPx = reducedMotion
        ? 0
        : Math.min(fearToShiverAmplitudePx(interpolated), currentEnvelopePx) * scale;

      // Direct CSS custom property write — bypasses React reconciliation so
      // the per-frame update is essentially free at the React layer. (Same
      // pattern BubbleLayer uses for --bx / --by.)
      vignetteRef.current?.style.setProperty(
        '--vignette-opacity',
        String(opacity),
      );
      audioManager.setHeartbeatBpm(bpm);

      // Sprite shiver: phase-offset sin/cos pair so the motion is a tilted
      // ellipse rather than axial wobble. The cos-arm is offset by π/3 so
      // peak-x and peak-y don't coincide on the same frame.
      const phase = (performance.now() / 1000) * P6_SHIVER_HZ * Math.PI;
      const dx = Math.sin(phase) * shiverPx;
      const dy = Math.cos(phase + Math.PI / 3) * shiverPx;
      const scene = getGameSceneRef() as { setPlayerShiver?: (dx: number, dy: number) => void } | null;
      scene?.setPlayerShiver?.(dx, dy);

      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    return (): void => {
      offAi();
      offTick();
      cancelAnimationFrame(raf);
    };
    // intentionally empty deps — subscribe once at mount.
  }, [reducedMotion]);

  return (
    <div
      ref={vignetteRef}
      className="fear-feedback-vignette"
      data-testid="fear-feedback-vignette"
      aria-hidden="true"
    />
  );
}
